import { MessageCircle, Mic, Volume2, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useEffect } from "react";

const TalkingWebsiteFeature = () => {
  useEffect(() => {
    // Ensure widget initializes after component mounts
    const widgetContainer = document.getElementById('voice-widget-container');
    if (widgetContainer && !widgetContainer.hasChildNodes()) {
      // Trigger widget initialization by dispatching a custom event
      window.dispatchEvent(new Event('DOMContentLoaded'));
    }
  }, []);
  return (
    <section className="py-12 space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
          <Sparkles className="w-5 h-5 text-primary animate-pulse" />
          <span className="text-primary font-bold">NEW TECHNOLOGY</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-foreground">
          🗣️ Experience Our Talking Website
        </h2>
        <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
          The world's first AI voice-powered robotics website. Talk to our AI assistant, ask questions, 
          and get instant answers about our robots and services — all without typing!
        </p>
      </div>

      {/* Feature Cards Grid */}
      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 hover:border-primary/40 transition-all duration-300">
          <div className="space-y-3">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Mic className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">Voice Commands</h3>
            <p className="text-muted-foreground text-sm">
              Just click the widget and speak naturally. Ask about pricing, features, or schedule a demo.
            </p>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-cyan-500/5 to-cyan-500/10 border-cyan-500/20 hover:border-cyan-500/40 transition-all duration-300">
          <div className="space-y-3">
            <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="text-xl font-bold text-foreground">Instant Answers</h3>
            <p className="text-muted-foreground text-sm">
              Get immediate responses to your questions about our robots, pricing, and automation solutions.
            </p>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-500/5 to-purple-500/10 border-purple-500/20 hover:border-purple-500/40 transition-all duration-300">
          <div className="space-y-3">
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Volume2 className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold text-foreground">Natural Conversations</h3>
            <p className="text-muted-foreground text-sm">
              Have a real conversation with our AI. It understands context and provides personalized recommendations.
            </p>
          </div>
        </Card>
      </div>

      {/* Voice Widget Highlight */}
      <div className="relative max-w-2xl mx-auto">
        <Card className="p-8 bg-gradient-to-br from-primary/10 via-cyan-500/5 to-purple-500/10 border-2 border-primary/30 relative overflow-hidden">
          {/* Animated glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-cyan-500/20 to-purple-500/20 animate-pulse opacity-50" />
          
          <div className="relative z-10 space-y-6">
            <div className="text-center space-y-3">
              <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                👇 Try It Now — Just Click & Talk!
              </h3>
              <p className="text-muted-foreground">
                Click the voice widget below to start a conversation with our AI assistant
              </p>
            </div>

            {/* Widget Container */}
            <div className="flex justify-center items-center min-h-[100px] p-6 rounded-lg bg-background/50 backdrop-blur-sm border border-primary/20">
              <div 
                id="voice-widget-container" 
                data-widget-key="b13e2374-6bd9-4e24-b912-324b69f66b47"
                className="inline-flex items-center justify-center"
              ></div>
            </div>

            {/* Instructions */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                💡 <strong>Pro Tip:</strong> Ask questions like "What robots do you offer?", 
                "How much does BellaBot cost?", or "Schedule a demo for next week"
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Benefits Banner */}
      <div className="max-w-4xl mx-auto">
        <Card className="p-6 bg-gradient-to-r from-primary/5 to-cyan-500/5 border-primary/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-bold text-foreground">Built with Neil AI Technology</h4>
                <p className="text-sm text-muted-foreground">Same AI that powers our automation solutions</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-primary font-bold">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Live & Ready to Help
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default TalkingWebsiteFeature;
